import { AutoAwesome } from '@mui/icons-material'
import { Chip, Skeleton, Stack, Typography } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { PinnablePopover } from '@omnea-app/omui/PinnablePopover'
import { PreviewCard } from '@omnea-app/omui/PreviewCard'
import { getStatusPillLabel } from '@omnea-app/packages/universal/requests/operationalStatusPill'
import { useGetRequestOperationalStatus } from './hooks/useGetRequestOperationalStatus'
import type { ChipProps } from '@mui/material'
import type { StatusPillKey } from '@omnea-app/packages/universal/requests/operationalStatusPill'
import type { ReactNode } from 'react'

type Props = {
  requestId: string
  pillKey: StatusPillKey
  isAiSet: boolean
}

const PILL_COLOR: Record<StatusPillKey, ChipProps['color']> = {
  BLOCKED: 'error',
  NEEDS_ATTENTION: 'orange',
  SLOW: 'warning',
  ON_TRACK: 'success',
  PENDING: 'neutral',
  COMPLETED: 'neutral',
  REJECTED: 'neutral',
}

const Section = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={1}>
    <Typography variant="body-medium" color="text.muted">
      {label}
    </Typography>
    <Typography
      variant="body-regular"
      color="text.default"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', textWrap: 'pretty' }}
    >
      {children}
    </Typography>
  </Stack>
)

const Footer = ({ updatedAt, nextGenerationAt }: { updatedAt: string | null; nextGenerationAt: Date | null }) => {
  const { t } = useTranslation('requests')
  const parsedUpdatedAt = updatedAt ? new Date(updatedAt) : null
  const lastRefresh =
    parsedUpdatedAt && !Number.isNaN(parsedUpdatedAt.getTime()) ? formatDistanceToNow(parsedUpdatedAt, { addSuffix: true }) : '—'
  const hasValidNext = nextGenerationAt && !Number.isNaN(nextGenerationAt.getTime())
  const nextRefresh = !hasValidNext
    ? t('table-column.status-popover.awaiting-activity')
    : nextGenerationAt.getTime() <= Date.now()
      ? t('table-column.status-popover.imminent')
      : formatDistanceToNow(nextGenerationAt, { addSuffix: true })
  return (
    <Stack direction="row" justifyContent="space-between" gap={4}>
      <Stack>
        <Typography variant="small-medium" color="text.subtle">
          {t('table-column.status-popover.last-refresh')}
        </Typography>
        <Typography variant="small-regular" color="text.muted">
          {lastRefresh}
        </Typography>
      </Stack>
      <Stack alignItems="flex-end">
        <Typography variant="small-medium" color="text.subtle">
          {t('table-column.status-popover.next-refresh')}
        </Typography>
        <Typography variant="small-regular" color="text.muted">
          {nextRefresh}
        </Typography>
      </Stack>
    </Stack>
  )
}

type OperationalStatus = NonNullable<ReturnType<typeof useGetRequestOperationalStatus>['data']>

const getEmptyStateKey = ({
  status,
  isFailedWithNoPrior,
  pillMismatchesStored,
}: {
  status: OperationalStatus['status'] | undefined
  isFailedWithNoPrior: boolean
  pillMismatchesStored: boolean
}): string => {
  if (isFailedWithNoPrior) return 'table-column.status-popover.failed'
  // Pill flipped faster than the detail cache; stored assessment no longer matches.
  if (pillMismatchesStored) return 'table-column.status-popover.syncing'
  // GENERATED but blank means an override cleared the detail.
  if (status === 'GENERATED') return 'table-column.status-popover.no-detail'
  if (status === 'PENDING') return 'table-column.status-popover.refreshing'
  return 'table-column.status-popover.no-status'
}

const OperationalStatusPopover = ({ requestId, pillKey }: { requestId: string; pillKey: StatusPillKey }) => {
  const { t } = useTranslation('requests')
  // PinnablePopover unmounts its children when closed, so mounting is already the "is open" signal —
  // gating the query on a separate isOpen prop would leave it disabled for the first painted frame.
  const { data, isLoading, isError } = useGetRequestOperationalStatus({ requestId })

  if (isLoading) return <PreviewCard content={<Skeleton variant="rectangular" height={80} />} />

  if (isError) {
    return (
      <PreviewCard
        content={
          <Typography variant="body-regular" color="text.default">
            {t('table-column.status-popover.load-failed')}
          </Typography>
        }
      />
    )
  }

  const pillMismatchesStored = data?.assessment != null && pillKey !== data.assessment
  const isFailedWithNoPrior = data?.status === 'FAILED' && !data.summary && !data.recommendation
  const isContentlessEmpty =
    data == null || data.status === 'PENDING' || pillMismatchesStored || (!data.summary && !data.recommendation)

  if (isContentlessEmpty) {
    const emptyKey = getEmptyStateKey({ status: data?.status, isFailedWithNoPrior, pillMismatchesStored })
    return (
      <PreviewCard
        content={
          <Typography variant="body-regular" color={isFailedWithNoPrior ? 'text.danger.default' : 'text.default'}>
            {t(emptyKey)}
          </Typography>
        }
      />
    )
  }

  const nextGen = data.nextGenerationAt ? new Date(data.nextGenerationAt) : null
  const updatedAt = data.status !== 'PENDING' ? (data.updatedAt ?? null) : null

  return (
    <PreviewCard
      content={
        <Stack gap={3}>
          {data.status === 'FAILED' && (
            <Typography variant="body-medium" color="text.danger.default">
              {t('table-column.status-popover.failed-with-prior')}
            </Typography>
          )}
          {data.summary && <Section label={t('table-column.status-popover.summary')}>{data.summary}</Section>}
          {data.recommendation && (
            <Section label={t('table-column.status-popover.recommendation')}>{data.recommendation.value}</Section>
          )}
        </Stack>
      }
      footer={<Footer updatedAt={updatedAt} nextGenerationAt={nextGen} />}
    />
  )
}

export const OperationalStatusCell = ({ requestId, pillKey, isAiSet }: Props) => {
  const { t } = useTranslation('requests')
  const isTerminal = pillKey === 'COMPLETED' || pillKey === 'REJECTED'

  const pillLabel = getStatusPillLabel(pillKey, t)

  const pillChip = (
    <Chip
      size="small"
      color={PILL_COLOR[pillKey]}
      label={pillLabel}
      icon={isAiSet ? <AutoAwesome /> : undefined}
      aria-label={pillLabel}
    />
  )

  if (isTerminal) return pillChip

  return (
    <PinnablePopover label={t('table-column.status-popover.aria-label')} trigger={pillChip}>
      <OperationalStatusPopover requestId={requestId} pillKey={pillKey} />
    </PinnablePopover>
  )
}
